import React, { useEffect, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import api from '../api';
import { writePrintDoc } from '../utils/printUtils';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Plus, Banknote, IndianRupee, Eye, AlertCircle, Edit, Trash2, Printer, FileUp, Download, CheckCircle } from 'lucide-react';
import { useAuth } from '../AuthContext';
import DeleteConfirmDialog from '../components/DeleteConfirmDialog';
import useAutoRefresh from '../hooks/useAutoRefresh';

const Loans = () => {
  const { hasPermission, isAdmin } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [fromPlanId, setFromPlanId] = useState(null);
  const [loans, setLoans] = useState([]);
  const [entrepreneurs, setEntrepreneurs] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [collectionDialogOpen, setCollectionDialogOpen] = useState(false);
  const [selectedLoan, setSelectedLoan] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editingLoan, setEditingLoan] = useState(null);
  
  // Delete confirmation
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [selectedBranchFilter, setSelectedBranchFilter] = useState('all');

  // Bulk loan import state
  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkBranch, setBulkBranch] = useState('');
  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);

  const [formData, setFormData] = useState({
    entrepreneur_id: '',
    loan_type: 'CEF',
    loan_amount: 0,
    interest_rate: 12,
    tenure_months: 12,
    repayment_frequency: 'monthly', // monthly or daily (daily only for Leirik)
    tenure_days: 0,
    disbursement_date: new Date().toISOString().split('T')[0],
    disbursement_mode: 'Bank',   // "Cash" or "Bank" — how funds were physically paid out
    moratorium_months: 0,        // grace period in months before EMI repayment starts
  });

  // Disburse dialog — search & filter state for the entrepreneur picker
  const [entSearch, setEntSearch] = useState('');
  const [entBranchFilter, setEntBranchFilter] = useState('all');
  const [entCrpEpFilter, setEntCrpEpFilter] = useState('all');
  const [crpEpList, setCrpEpList] = useState([]);

  const [collectionData, setCollectionData] = useState({
    collection_type: 'full_emi', // full_emi, principal_only, interest_only, penalty
    principal_amount: 0,
    interest_amount: 0,
    penalty_amount: 0,
    amount: 0,
    collection_date: new Date().toISOString().split('T')[0],
    payment_mode: 'Cash',
    receipt_number: '',
  });

  // Check if selected entrepreneur belongs to Leirik Microfinance (BR10)
  const isLeirikEntrepreneur = () => {
    if (!formData.entrepreneur_id) return false;
    const selectedEnt = entrepreneurs.find(e => e.id === formData.entrepreneur_id);
    if (!selectedEnt) return false;
    const branch = branches.find(b => b.id === selectedEnt.branch_id);
    return branch?.code === 'BR10';
  };

  useEffect(() => {
    fetchLoans();
    fetchEntrepreneurs();
    fetchBranches();
    fetchCrpEpStaff();
  }, []);

  useEffect(() => {
    if (branches.length > 0) fetchLoans();
  }, [selectedBranchFilter]);

  // Real-time auto-refresh
  useAutoRefresh(() => fetchLoans());

  // Prefill from Business Plan via query params (?from_plan=X&entrepreneur_id=Y&prefill_amount=Z&prefill_tenure=W)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromPlan = params.get('from_plan');
    const entId = params.get('entrepreneur_id');
    if (fromPlan && entId) {
      setFromPlanId(fromPlan);
      setFormData(prev => ({
        ...prev,
        entrepreneur_id: entId,
        loan_amount: Number(params.get('prefill_amount') || 0),
        tenure_months: Number(params.get('prefill_tenure') || 12),
      }));
      setDialogOpen(true);
      // Clear the URL so a refresh doesn't reopen
      navigate('/loans', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchBranches = async () => {
    try {
      const response = await api.get('/branches');
      setBranches(response.data);
    } catch (error) {
      console.error('Failed to fetch branches');
    }
  };

  const fetchLoans = async () => {
    try {
      const params = {};
      if (isAdmin() && selectedBranchFilter !== 'all') {
        params.branch_id = selectedBranchFilter;
      }
      const response = await api.get('/loans', { params });
      setLoans(response.data);
    } catch (error) {
      toast.error('Failed to fetch loans');
    } finally {
      setLoading(false);
    }
  };

  const fetchEntrepreneurs = async () => {
    try {
      const response = await api.get('/entrepreneurs', { params: { limit: 1000 } });
      setEntrepreneurs(response.data);
    } catch (error) {
      console.error('Failed to fetch entrepreneurs');
    }
  };

  const fetchCrpEpStaff = async () => {
    try {
      const r = await api.get('/staff', { params: { limit: 5000 } });
      setCrpEpList((r.data || []).filter(s => (s.staff_type || '').toLowerCase().includes('crp')));
    } catch (e) {
      console.error('Failed to fetch CRP-EP staff');
    }
  };

  // Handle entrepreneur selection - auto-fill loan type from enrollment
  // CEF fund balance for the selected entrepreneur's branch — shown in the disburse dialog as a guard.
  const [cefBalance, setCefBalance] = useState(null);
  const fetchCefBalance = async (branchId) => {
    if (!branchId) { setCefBalance(null); return; }
    try {
      const r = await api.get(`/loans/cef-balance/${branchId}`);
      setCefBalance(r.data?.balance ?? 0);
    } catch {
      setCefBalance(null);
    }
  };

  const handleEntrepreneurSelect = (entrepreneurId) => {
    const selectedEntrepreneur = entrepreneurs.find(e => e.id === entrepreneurId);
    if (selectedEntrepreneur?.branch_id) fetchCefBalance(selectedEntrepreneur.branch_id);
    if (selectedEntrepreneur && selectedEntrepreneur.enterprise?.loan_type) {
      setFormData({
        ...formData,
        entrepreneur_id: entrepreneurId,
        loan_type: selectedEntrepreneur.enterprise.loan_type,
      });
    } else {
      setFormData({
        ...formData,
        entrepreneur_id: entrepreneurId,
      });
    }
  };

  // Calculate EMI using NRLM SHG Reducing Balance Method
  // Fixed Principal = Loan Amount / Tenure
  // Interest = Outstanding Balance × Monthly Rate (reduces each month)
  const calculateReducingBalanceEMI = () => {
    const { loan_amount, interest_rate, tenure_months, repayment_frequency, tenure_days } = formData;
    
    if ((repayment_frequency === 'daily' || repayment_frequency === 'weekly' || repayment_frequency === 'bi-weekly') && isLeirikEntrepreneur()) {
      if (!loan_amount || !interest_rate || !tenure_days || tenure_days <= 0) return null;
      
      const P = loan_amount;
      const n = tenure_days;
      
      if (repayment_frequency === 'daily') {
        const r = interest_rate / 365 / 100;
        const principalPerDay = P / n;
        const firstDayEMI = principalPerDay + (P * r);
        const lastDayEMI = principalPerDay + (principalPerDay * r);
        let totalInterest = 0;
        let outstanding = P;
        for (let day = 0; day < n; day++) {
          totalInterest += outstanding * r;
          outstanding -= principalPerDay;
        }
        return {
          frequency: 'daily',
          label: 'Daily',
          installments: n,
          principalPerInstallment: principalPerDay,
          firstEMI: firstDayEMI,
          lastEMI: lastDayEMI,
          totalInterest: totalInterest,
          totalPayable: P + totalInterest,
          tenureDays: n
        };
      } else if (repayment_frequency === 'weekly') {
        const totalWeeks = Math.ceil(n / 7);
        const r = interest_rate / 52 / 100; // Weekly interest rate
        const principalPerWeek = P / totalWeeks;
        const firstWeekEMI = principalPerWeek + (P * r);
        const lastWeekEMI = principalPerWeek + (principalPerWeek * r);
        let totalInterest = 0;
        let outstanding = P;
        for (let w = 0; w < totalWeeks; w++) {
          totalInterest += outstanding * r;
          outstanding -= principalPerWeek;
        }
        return {
          frequency: 'weekly',
          label: 'Weekly',
          installments: totalWeeks,
          principalPerInstallment: principalPerWeek,
          firstEMI: firstWeekEMI,
          lastEMI: lastWeekEMI,
          totalInterest: totalInterest,
          totalPayable: P + totalInterest,
          tenureDays: n
        };
      } else { // bi-weekly
        const totalBiWeeks = Math.ceil(n / 14);
        const r = interest_rate / 26 / 100; // Bi-weekly interest rate (26 bi-weeks/year)
        const principalPerBiWeek = P / totalBiWeeks;
        const firstBiWeekEMI = principalPerBiWeek + (P * r);
        const lastBiWeekEMI = principalPerBiWeek + (principalPerBiWeek * r);
        let totalInterest = 0;
        let outstanding = P;
        for (let bw = 0; bw < totalBiWeeks; bw++) {
          totalInterest += outstanding * r;
          outstanding -= principalPerBiWeek;
        }
        return {
          frequency: 'bi-weekly',
          label: 'Bi-Weekly',
          installments: totalBiWeeks,
          principalPerInstallment: principalPerBiWeek,
          firstEMI: firstBiWeekEMI,
          lastEMI: lastBiWeekEMI,
          totalInterest: totalInterest,
          totalPayable: P + totalInterest,
          tenureDays: n
        };
      }
    }
    
    // Monthly repayment calculation (default)
    if (!loan_amount || !interest_rate || !tenure_months) return null;
    
    const P = loan_amount;
    const r = interest_rate / 12 / 100; // Monthly interest rate
    const n = tenure_months;
    
    // Fixed principal per month
    const principalPerMonth = P / n;
    
    // First month EMI (highest) = Fixed Principal + Interest on full balance
    const firstMonthEMI = principalPerMonth + (P * r);
    
    // Last month EMI (lowest) = Fixed Principal + Interest on last installment
    const lastMonthEMI = principalPerMonth + (principalPerMonth * r);
    
    // Calculate total interest using reducing balance
    let totalInterest = 0;
    let outstanding = P;
    for (let month = 0; month < n; month++) {
      totalInterest += outstanding * r;
      outstanding -= principalPerMonth;
    }
    
    return {
      frequency: 'monthly',
      principalPerMonth: principalPerMonth,
      firstMonthEMI: firstMonthEMI,
      lastMonthEMI: lastMonthEMI,
      totalInterest: totalInterest,
      totalPayable: P + totalInterest
    };
  };

  const emiDetails = calculateReducingBalanceEMI();

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      if (editMode && editingLoan) {
        await api.put(`/loans/${editingLoan.loan_id}`, {
          ...formData,
          disbursement_date: new Date(formData.disbursement_date).toISOString(),
        });
        toast.success('Loan updated successfully');
      } else {
        const created = await api.post('/loans', {
          ...formData,
          disbursement_date: new Date(formData.disbursement_date).toISOString(),
        });
        // If this loan was created from an approved Business Plan, link it back
        if (fromPlanId) {
          const newLoanId = created.data?.loan_id || created.data?.id;
          if (newLoanId) {
            try { await api.post(`/business-plans/${fromPlanId}/link-loan`, { loan_id: newLoanId }); }
            catch (e) { console.warn('Failed to link loan to business plan', e); }
          }
          setFromPlanId(null);
        }
        toast.success('Loan created successfully');
      }
      closeDialog();
      fetchLoans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save loan');
    }
  };

  const handleEdit = (loan) => {
    setEditingLoan(loan);
    setFormData({
      entrepreneur_id: loan.entrepreneur_id,
      loan_type: loan.loan_type,
      loan_amount: loan.loan_amount,
      interest_rate: loan.interest_rate,
      tenure_months: loan.tenure_months,
      repayment_frequency: loan.repayment_frequency || 'monthly',
      tenure_days: loan.tenure_days || 0,
      disbursement_date: new Date(loan.disbursement_date).toISOString().split('T')[0],
      disbursement_mode: loan.disbursement_mode || 'Bank',
      moratorium_months: loan.moratorium_months || 0,
    });
    setEditMode(true);
    setDialogOpen(true);
  };

  const handleDelete = async (loanId) => {
    try {
      await api.delete(`/loans/${loanId}`);
      toast.success('Loan deleted successfully');
      setDeleteDialogOpen(false);
      setItemToDelete(null);
      fetchLoans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete loan');
    }
  };

  const openDeleteDialog = (loan) => {
    setItemToDelete(loan);
    setDeleteDialogOpen(true);
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setEditMode(false);
    setEditingLoan(null);
    setEntSearch('');
    setEntBranchFilter('all');
    setEntCrpEpFilter('all');
    setFormData({
      entrepreneur_id: '',
      loan_type: 'CEF',
      loan_amount: 0,
      interest_rate: 12,
      tenure_months: 12,
      repayment_frequency: 'monthly',
      tenure_days: 0,
      disbursement_date: new Date().toISOString().split('T')[0],
      disbursement_mode: 'Bank',
      moratorium_months: 0,
    });
  };

  const handleCollectionSubmit = async (e) => {
    e.preventDefault();

    try {
      await api.post('/loan-collections', {
        loan_id: selectedLoan.loan_id,
        ...collectionData,
        collection_date: new Date(collectionData.collection_date).toISOString(),
      });

      toast.success('Collection recorded successfully');
      setCollectionDialogOpen(false);
      setCollectionData({
        collection_type: 'full_emi',
        principal_amount: 0,
        interest_amount: 0,
        penalty_amount: 0,
        amount: 0,
        collection_date: new Date().toISOString().split('T')[0],
        payment_mode: 'Cash',
        receipt_number: '',
      });
      fetchLoans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to record collection');
    }
  };


  const downloadLoanTemplate = async () => {
    try {
      const response = await api.get('/loan-bulk/template', { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'loan_bulk_template.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Template downloaded');
    } catch (error) {
      toast.error('Failed to download template');
    }
  };

  const handleBulkLoanUpload = async () => {
    if (!bulkFile) { toast.error('Please select a file'); return; }
    if (isAdmin() && !bulkBranch) { toast.error('Please select a branch'); return; }
    setBulkUploading(true);
    setBulkResult(null);
    try {
      const formData = new FormData();
      formData.append('file', bulkFile);
      formData.append('branch_id', isAdmin() ? bulkBranch : 'auto');
      const response = await api.post('/loan-bulk/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setBulkResult(response.data);
      if (response.data.imported > 0) {
        toast.success(`${response.data.imported} loans imported successfully`);
        fetchLoans();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to upload file');
    } finally {
      setBulkUploading(false);
    }
  };

  const closeBulkDialog = () => {
    setBulkDialogOpen(false);
    setBulkFile(null);
    setBulkBranch('');
    setBulkResult(null);
  };

  return (
    <div className="p-4 md:p-6" data-testid="loans-page">
      <div className="mb-4 flex flex-col md:flex-row md:items-center md:justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Loans Management</h1>
          <p className="text-sm text-slate-600">Manage loan disbursements and collections</p>
        </div>

        <div className="flex flex-wrap gap-2 items-center">
          {isAdmin() && (
            <Select value={selectedBranchFilter} onValueChange={setSelectedBranchFilter}>
              <SelectTrigger className="w-48" data-testid="branch-filter-loans">
                <SelectValue placeholder="All Branches" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Branches</SelectItem>
                {branches.map(b => (
                  <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}
          <Button
            variant="outline"
            onClick={() => setBulkDialogOpen(true)}
            data-testid="bulk-loan-upload-button"
            className="border-indigo-300 text-indigo-700 hover:bg-indigo-50"
          >
            <FileUp className="w-4 h-4 mr-2" />
            Bulk Import
          </Button>
          {isAdmin() && (
            <Button
              variant="outline"
              onClick={() => {
                const htmlContent = `
                    <html>
                      <head>
                        <title>Loans Report - WWAGS MIS</title>
                        <style>
                          body { font-family: Arial, sans-serif; padding: 20px; }
                          h1 { color: #047857; font-size: 20px; margin-bottom: 5px; }
                          p { color: #666; font-size: 12px; margin-bottom: 15px; }
                          table { width: 100%; border-collapse: collapse; font-size: 11px; }
                          th { background: #047857; color: white; padding: 8px; text-align: left; }
                          td { padding: 6px 8px; border-bottom: 1px solid #ddd; }
                          tr:nth-child(even) { background: #f9f9f9; }
                          .amount { text-align: right; }
                          .status { padding: 2px 8px; border-radius: 10px; font-size: 10px; }
                          .active { background: #d1fae5; color: #047857; }
                          .closed { background: #f1f5f9; color: #475569; }
                          @media print { body { -webkit-print-color-adjust: exact; print-color-adjust: exact; } }
                        </style>
                      </head>
                      <body>
                        <h1>WWAGS MIS - Loans Report</h1>
                        <p>Generated on: ${new Date().toLocaleString('en-IN')}</p>
                        <table>
                          <thead>
                            <tr>
                              <th>Loan ID</th>
                              <th>Entrepreneur</th>
                              <th>Branch</th>
                              <th>Type</th>
                              <th class="amount">Amount</th>
                              <th class="amount">EMI</th>
                              <th class="amount">Outstanding</th>
                              <th>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            ${loans.map(loan => `
                              <tr>
                                <td>${loan.loan_id}</td>
                                <td>${loan.entrepreneur_name}</td>
                                <td>${branches.find(b => b.id === loan.branch_id)?.code || '-'}</td>
                                <td>${loan.loan_type}</td>
                                <td class="amount">Rs.${loan.loan_amount.toLocaleString('en-IN')}</td>
                                <td class="amount">Rs.${loan.emi_amount?.toLocaleString('en-IN')}</td>
                                <td class="amount">Rs.${loan.outstanding_amount.toLocaleString('en-IN')}</td>
                                <td><span class="status ${loan.status}">${loan.status}</span></td>
                              </tr>
                            `).join('')}
                          </tbody>
                        </table>
                      </body>
                    </html>
                `;
                const printWindow = window.open('', '_blank');
                if (!printWindow) {
                  const iframe = document.createElement('iframe');
                  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;';
                  document.body.appendChild(iframe);
                  writePrintDoc(iframe.contentDocument, htmlContent);
                  setTimeout(() => { iframe.contentWindow.print(); setTimeout(() => document.body.removeChild(iframe), 1000); }, 500);
                  return;
                }
                writePrintDoc(printWindow.document, htmlContent);
                setTimeout(() => printWindow.print(), 500);
              }}
              data-testid="print-loans-button"
            >
              <Printer className="w-4 h-4 mr-2" />
              Print
            </Button>
          )}

          <Dialog open={dialogOpen} onOpenChange={(open) => { if (!open) closeDialog(); else setDialogOpen(true); }}>
            <DialogTrigger asChild>
              <Button data-testid="add-loan-button" className="bg-emerald-700 hover:bg-emerald-800 text-white">
                <Plus className="w-4 h-4 mr-2" />
                Disburse Loan
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl">
              <DialogHeader>
                <DialogTitle>{editMode ? 'Edit Loan' : 'Disburse New Loan'}</DialogTitle>
              </DialogHeader>

              <form onSubmit={handleSubmit} className="space-y-6">
                <div className="space-y-2" data-testid="entrepreneur-picker">
                  <Label>Select Entrepreneur *</Label>

                  {/* Filters: Branch + CRP-EP + Search — only shown for new disbursals */}
                  {!editMode && (
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-2 p-2 bg-slate-50 border border-slate-200 rounded-md">
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Filter by Branch</Label>
                        <Select value={entBranchFilter} onValueChange={setEntBranchFilter}>
                          <SelectTrigger className="h-8 text-xs" data-testid="ent-branch-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All Branches</SelectItem>
                            {branches.map(b => <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Filter by CRP-EP</Label>
                        <Select value={entCrpEpFilter} onValueChange={setEntCrpEpFilter}>
                          <SelectTrigger className="h-8 text-xs" data-testid="ent-crpep-filter"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="all">All CRP-EPs</SelectItem>
                            <SelectItem value="__none__">— No CRP-EP assigned —</SelectItem>
                            {crpEpList.map(s => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label className="text-[10px] uppercase tracking-wide text-slate-500">Search</Label>
                        <Input value={entSearch} onChange={e => setEntSearch(e.target.value)} placeholder="ID / Name / Enterprise…" className="h-8 text-xs" data-testid="ent-search" />
                      </div>
                    </div>
                  )}

                  {/* Searchable, filtered list */}
                  {!editMode ? (
                    (() => {
                      const q = entSearch.trim().toLowerCase();
                      const filtered = entrepreneurs.filter(e => {
                        if (entBranchFilter !== 'all' && e.branch_id !== entBranchFilter) return false;
                        if (entCrpEpFilter === '__none__' && e.crp_ep_id) return false;
                        if (entCrpEpFilter !== 'all' && entCrpEpFilter !== '__none__' && e.crp_ep_id !== entCrpEpFilter) return false;
                        if (!q) return true;
                        const hay = `${e.enrollment_no || ''} ${e.name || ''} ${e.enterprise?.name || ''} ${e.mobile || ''}`.toLowerCase();
                        return hay.includes(q);
                      });
                      const selected = entrepreneurs.find(e => e.id === formData.entrepreneur_id);
                      return (
                        <>
                          <div className="max-h-56 overflow-y-auto border border-slate-200 rounded-md divide-y divide-slate-100" data-testid="ent-results-list">
                            {filtered.length === 0 ? (
                              <div className="text-xs text-slate-500 px-3 py-4 text-center">No entrepreneurs match your filters</div>
                            ) : filtered.slice(0, 100).map(e => {
                              const isSel = e.id === formData.entrepreneur_id;
                              const br = branches.find(b => b.id === e.branch_id);
                              return (
                                <button type="button" key={e.id}
                                  onClick={() => handleEntrepreneurSelect(e.id)}
                                  className={`w-full text-left px-3 py-2 text-sm hover:bg-emerald-50 flex items-center justify-between ${isSel ? 'bg-emerald-100' : ''}`}
                                  data-testid={`ent-result-${e.id}`}
                                >
                                  <div>
                                    <div className="font-medium text-slate-900">
                                      <span className="text-emerald-700">{e.enrollment_no || '—'}</span> · {e.name}
                                      {isSel && <span className="ml-2 text-[10px] uppercase tracking-wide text-emerald-700 font-bold">selected</span>}
                                    </div>
                                    <div className="text-[11px] text-slate-500">
                                      {e.enterprise?.name || '—'}
                                      {br ? <> · <span className="text-slate-600">{br.name}</span></> : null}
                                      {e.crp_ep_name ? <> · CRP-EP: <span className="text-slate-600">{e.crp_ep_name}</span></> : null}
                                    </div>
                                  </div>
                                  <span className="text-[10px] text-slate-400">{e.mobile || ''}</span>
                                </button>
                              );
                            })}
                          </div>
                          {filtered.length > 100 && (
                            <p className="text-[10px] text-slate-500 italic">Showing first 100 — refine filters to see more.</p>
                          )}
                          {selected && (
                            <div className="text-xs bg-emerald-50 border border-emerald-200 rounded p-2 text-emerald-800" data-testid="ent-selected-summary">
                              ✓ Selected: <b>{selected.enrollment_no} · {selected.name}</b> — {selected.enterprise?.name}
                            </div>
                          )}
                        </>
                      );
                    })()
                  ) : (
                    // In edit mode keep the simple read-only select
                    <Select value={formData.entrepreneur_id} onValueChange={handleEntrepreneurSelect} disabled={true}>
                      <SelectTrigger data-testid="entrepreneur-select"><SelectValue placeholder="Choose entrepreneur" /></SelectTrigger>
                      <SelectContent>
                        {entrepreneurs.map((ent) => (
                          <SelectItem key={ent.id} value={ent.id}>{ent.name} - {ent.enterprise.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  )}
                </div>

                {/* CEF Fund balance — mandatory check when loan_type is CEF */}
                {!editMode && formData.loan_type === 'CEF' && cefBalance !== null && (() => {
                  const need = Number(formData.loan_amount || 0);
                  const ok = need > 0 && cefBalance >= need;
                  const noAmt = need <= 0;
                  return (
                    <div
                      className={`rounded-md border-l-4 p-3 flex items-start gap-2 text-sm ${
                        noAmt ? 'bg-slate-50 border-slate-300' : ok ? 'bg-emerald-50 border-emerald-500' : 'bg-amber-50 border-amber-500'
                      }`}
                      data-testid="cef-balance-banner"
                    >
                      <div className="flex-1">
                        <p className={`font-semibold ${noAmt ? 'text-slate-700' : ok ? 'text-emerald-900' : 'text-amber-900'}`}>
                          {noAmt ? 'CEF Fund Balance' : ok ? 'CEF balance is sufficient' : 'Low CEF balance — disbursal still allowed'}
                        </p>
                        <p className={`text-xs ${noAmt ? 'text-slate-600' : ok ? 'text-emerald-700' : 'text-amber-800'}`}>
                          Available CEF for this branch: <b>Rs. {Number(cefBalance).toLocaleString('en-IN')}</b>
                          {!noAmt && <> · Loan amount: <b>Rs. {need.toLocaleString('en-IN')}</b></>}
                          {!noAmt && !ok && <> · Shortfall: <b>Rs. {(need - cefBalance).toLocaleString('en-IN')}</b></>}
                        </p>
                        {!noAmt && !ok && (
                          <p className="text-[11px] text-amber-700 mt-1">
                            CEF ledger will go negative. To keep books in sync, add a CEF fund receipt for this branch:{' '}
                            <a
                              href="/funds"
                              className="underline font-semibold text-amber-900 hover:text-amber-950"
                              data-testid="cef-topup-link"
                            >Funds → Add Fund Receipt → &ldquo;Total Fund Received CEF / Balance&rdquo;</a>.
                          </p>
                        )}
                      </div>
                    </div>
                  );
                })()}

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="loan_type">Loan Type *</Label>
                    <Select
                      value={formData.loan_type}
                      onValueChange={(value) => setFormData({ ...formData, loan_type: value })}
                      disabled={!editMode && formData.entrepreneur_id !== ''}
                    >
                      <SelectTrigger className={!editMode && formData.entrepreneur_id !== '' ? 'bg-slate-100' : ''}>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="CEF">CEF</SelectItem>
                        <SelectItem value="CIF">CIF</SelectItem>
                        <SelectItem value="Bank">Bank</SelectItem>
                      </SelectContent>
                    </Select>
                    {!editMode && formData.entrepreneur_id !== '' && (
                      <p className="text-xs text-slate-500">Auto-filled from entrepreneur enrollment</p>
                    )}
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="loan_amount">Loan Amount (Rs.) *</Label>
                    <Input
                      id="loan_amount"
                      type="number"
                      value={formData.loan_amount}
                      onChange={(e) => setFormData({ ...formData, loan_amount: parseFloat(e.target.value) })}
                      required
                      data-testid="loan-amount-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="interest_rate">Interest Rate (%) *</Label>
                    <Input
                      id="interest_rate"
                      type="number"
                      step="0.01"
                      value={formData.interest_rate}
                      onChange={(e) => setFormData({ ...formData, interest_rate: parseFloat(e.target.value) })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="tenure_months">Tenure (Months) *</Label>
                    <Input
                      id="tenure_months"
                      type="number"
                      value={formData.tenure_months}
                      onChange={(e) => setFormData({ ...formData, tenure_months: parseInt(e.target.value) })}
                      required={formData.repayment_frequency !== 'daily'}
                      disabled={formData.repayment_frequency === 'daily'}
                      className={formData.repayment_frequency === 'daily' ? 'bg-slate-100' : ''}
                    />
                  </div>

                  {/* Daily Repayment Option - Only for Leirik Microfinance (BR10) */}
                  {isLeirikEntrepreneur() && (
                    <>
                      <div className="space-y-2">
                        <Label htmlFor="repayment_frequency">Repayment Frequency *</Label>
                        <Select
                          value={formData.repayment_frequency}
                          onValueChange={(value) => setFormData({ ...formData, repayment_frequency: value })}
                        >
                          <SelectTrigger data-testid="repayment-frequency-select">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="monthly">Monthly</SelectItem>
                            <SelectItem value="weekly">Weekly</SelectItem>
                            <SelectItem value="bi-weekly">Bi-Weekly (Every 2 Weeks)</SelectItem>
                            <SelectItem value="daily">Daily</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>

                      {(formData.repayment_frequency === 'daily' || formData.repayment_frequency === 'weekly' || formData.repayment_frequency === 'bi-weekly') && (
                        <div className="space-y-2">
                          <Label htmlFor="tenure_days">Tenure (Days) *</Label>
                          <Input
                            id="tenure_days"
                            type="number"
                            value={formData.tenure_days}
                            onChange={(e) => setFormData({ ...formData, tenure_days: parseInt(e.target.value) })}
                            required
                            placeholder="e.g. 90, 180, 365"
                            data-testid="tenure-days-input"
                          />
                        </div>
                      )}
                    </>
                  )}

                  <div className="space-y-2">
                    <Label htmlFor="disbursement_date">Disbursement Date *</Label>
                    <Input
                      id="disbursement_date"
                      type="date"
                      value={formData.disbursement_date}
                      onChange={(e) => setFormData({ ...formData, disbursement_date: e.target.value })}
                      required
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="disbursement_mode">Disbursement Mode *</Label>
                    <Select
                      value={formData.disbursement_mode || 'Bank'}
                      onValueChange={(value) => setFormData({ ...formData, disbursement_mode: value })}
                    >
                      <SelectTrigger id="disbursement_mode" data-testid="disbursement-mode-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="Bank">Bank Transfer</SelectItem>
                        <SelectItem value="Cash">Cash</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500">How the loan amount was physically paid to the entrepreneur.</p>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="moratorium_months">Moratorium Period</Label>
                    <Select
                      value={String(formData.moratorium_months ?? 0)}
                      onValueChange={(value) => setFormData({ ...formData, moratorium_months: parseInt(value) })}
                    >
                      <SelectTrigger id="moratorium_months" data-testid="moratorium-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="0">No Moratorium</SelectItem>
                        <SelectItem value="1">1 Month</SelectItem>
                        <SelectItem value="2">2 Months</SelectItem>
                        <SelectItem value="3">3 Months</SelectItem>
                        <SelectItem value="6">6 Months</SelectItem>
                      </SelectContent>
                    </Select>
                    <p className="text-[11px] text-slate-500">Grace period before first EMI starts. Default: 0 (no moratorium).</p>
                  </div>
                </div>

                {/* NRLM SHG Reducing Balance EMI Preview */}
                {emiDetails && emiDetails.frequency === 'monthly' && (
                  <div className="p-4 bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-lg" data-testid="emi-preview">
                    <div className="mb-2">
                      <p className="text-sm font-semibold text-emerald-800">NRLM SHG Reducing Balance EMI (Monthly)</p>
                      <p className="text-xs text-slate-500">Fixed Principal + Interest on Outstanding Balance</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white/60 rounded p-2">
                        <p className="text-xs text-slate-500">Fixed Principal/Month</p>
                        <p className="font-bold text-slate-800">₹{emiDetails.principalPerMonth.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                      </div>
                      <div className="bg-white/60 rounded p-2">
                        <p className="text-xs text-slate-500">Total Interest</p>
                        <p className="font-bold text-purple-700">₹{emiDetails.totalInterest.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                      </div>
                      <div className="bg-white/60 rounded p-2">
                        <p className="text-xs text-slate-500">1st Month EMI (Highest)</p>
                        <p className="font-bold text-emerald-700">₹{emiDetails.firstMonthEMI.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                      </div>
                      <div className="bg-white/60 rounded p-2">
                        <p className="text-xs text-slate-500">Last Month EMI (Lowest)</p>
                        <p className="font-bold text-blue-700">₹{emiDetails.lastMonthEMI.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-2 border-t border-emerald-200 flex justify-between items-center">
                      <span className="text-sm text-slate-600">Total Payable:</span>
                      <span className="text-lg font-bold text-emerald-800">
                        ₹{emiDetails.totalPayable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </div>
                )}

                {/* Daily/Weekly/Bi-Weekly Repayment EMI Preview - For Leirik Branch */}
                {emiDetails && (emiDetails.frequency === 'daily' || emiDetails.frequency === 'weekly' || emiDetails.frequency === 'bi-weekly') && (
                  <div className="p-4 bg-gradient-to-r from-orange-50 to-amber-50 border-2 border-orange-200 rounded-lg" data-testid="leirik-emi-preview">
                    <div className="mb-2">
                      <p className="text-sm font-semibold text-orange-800">{emiDetails.label} Reducing Balance EMI (Leirik Microfinance)</p>
                      <p className="text-xs text-slate-500">Fixed {emiDetails.label} Principal + Interest on Outstanding Balance | {emiDetails.installments} installments</p>
                    </div>
                    <div className="grid grid-cols-2 gap-3 text-sm">
                      <div className="bg-white/60 rounded p-2">
                        <p className="text-xs text-slate-500">Fixed Principal/{emiDetails.label}</p>
                        <p className="font-bold text-slate-800">₹{emiDetails.principalPerInstallment.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                      </div>
                      <div className="bg-white/60 rounded p-2">
                        <p className="text-xs text-slate-500">Total Interest</p>
                        <p className="font-bold text-purple-700">₹{emiDetails.totalInterest.toLocaleString('en-IN', { maximumFractionDigits: 0 })}</p>
                      </div>
                      <div className="bg-white/60 rounded p-2">
                        <p className="text-xs text-slate-500">1st Installment (Highest)</p>
                        <p className="font-bold text-orange-700">₹{emiDetails.firstEMI.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                      </div>
                      <div className="bg-white/60 rounded p-2">
                        <p className="text-xs text-slate-500">Last Installment (Lowest)</p>
                        <p className="font-bold text-blue-700">₹{emiDetails.lastEMI.toLocaleString('en-IN', { maximumFractionDigits: 2 })}</p>
                      </div>
                    </div>
                    <div className="mt-3 pt-2 border-t border-orange-200 flex justify-between items-center">
                      <span className="text-sm text-slate-600">Total Payable ({emiDetails.tenureDays} days / {emiDetails.installments} installments):</span>
                      <span className="text-lg font-bold text-orange-800">
                        ₹{emiDetails.totalPayable.toLocaleString('en-IN', { maximumFractionDigits: 0 })}
                      </span>
                    </div>
                  </div>
                )}

                <div className="flex justify-end gap-3">
                  <Button type="button" variant="outline" onClick={closeDialog}>
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    data-testid="submit-loan-button"
                    className="bg-emerald-700 hover:bg-emerald-800 text-white"
                  >
                    {editMode ? 'Update Loan' : 'Disburse Loan'}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Collection Dialog */}
      <Dialog open={collectionDialogOpen} onOpenChange={setCollectionDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Record Collection</DialogTitle>
          </DialogHeader>

          {selectedLoan && (
            <div className="bg-slate-50 rounded-lg p-4 mb-4 space-y-2">
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Loan ID:</span>
                <span className="font-medium text-slate-900">{selectedLoan.loan_id}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Borrower:</span>
                <span className="font-medium text-slate-900">{selectedLoan.entrepreneur_name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">EMI Amount:</span>
                <span className="font-medium text-emerald-700">₹{selectedLoan.emi_amount?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-slate-600">Outstanding Amount:</span>
                <span className="font-medium text-red-600">₹{selectedLoan.outstanding_amount?.toLocaleString('en-IN')}</span>
              </div>
              <div className="flex items-center gap-2 pt-2 border-t border-slate-200">
                <AlertCircle className="w-4 h-4 text-amber-500" />
                <span className="text-xs text-amber-600 font-medium">Minimum Due: ₹{selectedLoan.emi_amount?.toLocaleString('en-IN')}</span>
              </div>
            </div>
          )}

          <form onSubmit={handleCollectionSubmit} className="space-y-4">
            {/* Collection Type */}
            <div className="space-y-2">
              <Label>Collection Type *</Label>
              <Select
                value={collectionData.collection_type}
                onValueChange={(value) => {
                  let newData = { ...collectionData, collection_type: value };
                  if (value === 'full_emi' && selectedLoan) {
                    newData.amount = selectedLoan.emi_amount;
                    newData.principal_amount = 0;
                    newData.interest_amount = 0;
                    newData.penalty_amount = 0;
                  } else {
                    newData.amount = 0;
                  }
                  setCollectionData(newData);
                }}
              >
                <SelectTrigger data-testid="collection-type-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_emi">Full EMI (₹{selectedLoan?.emi_amount?.toLocaleString('en-IN')})</SelectItem>
                  <SelectItem value="custom">Custom Amount</SelectItem>
                  <SelectItem value="breakdown">Breakdown (Principal + Interest + Penalty)</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Full EMI / Custom Amount */}
            {(collectionData.collection_type === 'full_emi' || collectionData.collection_type === 'custom') && (
              <div className="space-y-2">
                <Label htmlFor="amount">Collection Amount (₹) *</Label>
                <Input
                  id="amount"
                  type="number"
                  value={collectionData.amount}
                  onChange={(e) => setCollectionData({ ...collectionData, amount: parseFloat(e.target.value) || 0 })}
                  required
                  readOnly={collectionData.collection_type === 'full_emi'}
                  data-testid="collection-amount-input"
                  className={collectionData.collection_type === 'full_emi' ? 'bg-slate-100' : ''}
                />
              </div>
            )}

            {/* Breakdown Fields */}
            {collectionData.collection_type === 'breakdown' && (
              <div className="space-y-3 p-4 bg-slate-50 rounded-lg">
                <div className="space-y-2">
                  <Label htmlFor="principal_amount">Principal Amount (₹)</Label>
                  <Input
                    id="principal_amount"
                    type="number"
                    value={collectionData.principal_amount}
                    onChange={(e) => {
                      const principal = parseFloat(e.target.value) || 0;
                      setCollectionData({ 
                        ...collectionData, 
                        principal_amount: principal,
                        amount: principal + collectionData.interest_amount + collectionData.penalty_amount
                      });
                    }}
                    data-testid="principal-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="interest_amount">Interest Amount (₹)</Label>
                  <Input
                    id="interest_amount"
                    type="number"
                    value={collectionData.interest_amount}
                    onChange={(e) => {
                      const interest = parseFloat(e.target.value) || 0;
                      setCollectionData({ 
                        ...collectionData, 
                        interest_amount: interest,
                        amount: collectionData.principal_amount + interest + collectionData.penalty_amount
                      });
                    }}
                    data-testid="interest-amount-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="penalty_amount">Penalty Amount (₹)</Label>
                  <Input
                    id="penalty_amount"
                    type="number"
                    value={collectionData.penalty_amount}
                    onChange={(e) => {
                      const penalty = parseFloat(e.target.value) || 0;
                      setCollectionData({ 
                        ...collectionData, 
                        penalty_amount: penalty,
                        amount: collectionData.principal_amount + collectionData.interest_amount + penalty
                      });
                    }}
                    data-testid="penalty-amount-input"
                  />
                </div>
                <div className="flex justify-between pt-2 border-t border-slate-300">
                  <span className="font-medium text-slate-700">Total Collection:</span>
                  <span className="font-bold text-emerald-700">₹{collectionData.amount.toLocaleString('en-IN')}</span>
                </div>
              </div>
            )}

            <div className="space-y-2">
              <Label htmlFor="collection_date">Collection Date *</Label>
              <Input
                id="collection_date"
                type="date"
                value={collectionData.collection_date}
                onChange={(e) => setCollectionData({ ...collectionData, collection_date: e.target.value })}
                required
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="payment_mode">Payment Mode *</Label>
              <Select
                value={collectionData.payment_mode}
                onValueChange={(value) => setCollectionData({ ...collectionData, payment_mode: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Cash">Cash</SelectItem>
                  <SelectItem value="Bank Transfer">Bank Transfer</SelectItem>
                  <SelectItem value="Cheque">Cheque</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label htmlFor="receipt_number">Receipt Number *</Label>
              <Input
                id="receipt_number"
                value={collectionData.receipt_number}
                onChange={(e) => setCollectionData({ ...collectionData, receipt_number: e.target.value })}
                required
              />
            </div>

            <div className="flex justify-end gap-3">
              <Button type="button" variant="outline" onClick={() => setCollectionDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-700 hover:bg-emerald-800 text-white"
                disabled={collectionData.amount <= 0}
              >
                Record Collection (₹{collectionData.amount.toLocaleString('en-IN')})
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-emerald-700"></div>
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-emerald-700 text-white">
                <tr>
                  <th className="px-3 py-2 text-left font-semibold">Loan ID</th>
                  <th className="px-3 py-2 text-left font-semibold">Entrepreneur</th>
                  {isAdmin() && <th className="px-3 py-2 text-left font-semibold">Branch</th>}
                  <th className="px-3 py-2 text-left font-semibold">Type</th>
                  <th className="px-3 py-2 text-right font-semibold">Amount</th>
                  <th className="px-3 py-2 text-right font-semibold">EMI</th>
                  <th className="px-3 py-2 text-right font-semibold">Outstanding</th>
                  <th className="px-3 py-2 text-center font-semibold">Status</th>
                  <th className="px-3 py-2 text-center font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {loans.length === 0 ? (
                  <tr>
                    <td colSpan={isAdmin() ? 9 : 8} className="px-4 py-8 text-center text-slate-500">
                      <Banknote className="w-10 h-10 mx-auto mb-2 text-slate-300" />
                      <p>No loans found</p>
                    </td>
                  </tr>
                ) : (
                  loans.map((loan) => (
                    <tr key={loan.id} className="hover:bg-slate-50" data-testid="loan-row">
                      <td className="px-3 py-2 font-mono text-xs text-emerald-700 font-medium">{loan.loan_id}</td>
                      <td className="px-3 py-2 font-medium text-slate-900">{loan.entrepreneur_name}</td>
                      {isAdmin() && (
                        <td className="px-3 py-2 text-xs">
                          <span className="px-2 py-0.5 rounded bg-slate-100 text-slate-700">
                            {branches.find(b => b.id === loan.branch_id)?.code || branches.find(b => b.id === loan.branch_id)?.name?.substring(0, 15) || '-'}
                          </span>
                        </td>
                      )}
                      <td className="px-3 py-2">
                        <span className="text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-800">
                          {loan.loan_type}
                        </span>
                        {loan.repayment_frequency === 'daily' && (
                          <span className="ml-1 text-xs px-2 py-0.5 rounded-full bg-orange-100 text-orange-800">
                            Daily
                          </span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-medium">₹{loan.loan_amount.toLocaleString('en-IN')}</td>
                      <td className="px-3 py-2 text-right font-medium">
                        {loan.repayment_frequency === 'daily' ? (
                          <span className="text-orange-700">₹{loan.daily_emi_amount?.toLocaleString('en-IN')}/day</span>
                        ) : (
                          <span className="text-emerald-700">₹{loan.emi_amount?.toLocaleString('en-IN')}</span>
                        )}
                      </td>
                      <td className="px-3 py-2 text-right font-semibold text-red-600">
                        ₹{loan.outstanding_amount.toLocaleString('en-IN')}
                      </td>
                      <td className="px-3 py-2 text-center">
                        <span
                          className={`text-xs px-2 py-0.5 rounded-full ${
                            loan.status === 'active'
                              ? 'bg-emerald-100 text-emerald-800'
                              : loan.status === 'closed'
                              ? 'bg-slate-100 text-slate-700'
                              : 'bg-red-100 text-red-800'
                          }`}
                        >
                          {loan.status}
                        </span>
                      </td>
                      <td className="px-3 py-2">
                        <div className="flex justify-center gap-0.5">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/loans/${loan.loan_id}`)}
                            data-testid="view-loan-button"
                            className="h-7 w-7 p-0 text-slate-600 hover:text-slate-900"
                          >
                            <Eye className="w-3.5 h-3.5" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleEdit(loan)}
                            data-testid="edit-loan-button"
                            className="h-7 w-7 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          {loan.status === 'active' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setSelectedLoan(loan);
                                setCollectionData({
                                  collection_type: 'full_emi',
                                  principal_amount: 0,
                                  interest_amount: 0,
                                  penalty_amount: 0,
                                  amount: loan.emi_amount,
                                  collection_date: new Date().toISOString().split('T')[0],
                                  payment_mode: 'Cash',
                                  receipt_number: '',
                                });
                                setCollectionDialogOpen(true);
                              }}
                              data-testid="record-collection-button"
                              className="h-7 w-7 p-0 text-emerald-700 hover:text-emerald-800 hover:bg-emerald-50"
                            >
                              <IndianRupee className="w-3.5 h-3.5" />
                            </Button>
                          )}
                          {isAdmin() && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDeleteDialog(loan)}
                              data-testid="delete-loan-button"
                              className="h-7 w-7 p-0 text-red-600 hover:text-red-700 hover:bg-red-50"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      <DeleteConfirmDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        onConfirm={() => itemToDelete && handleDelete(itemToDelete.loan_id)}
        title="Delete Loan"
        description="Are you sure you want to delete this loan record? This action cannot be undone and will remove all associated collections."
        itemName={`${itemToDelete?.loan_id} - ₹${itemToDelete?.loan_amount?.toLocaleString('en-IN')}`}
      />

      {/* Bulk Loan Import Dialog */}
      <Dialog open={bulkDialogOpen} onOpenChange={(open) => { if (!open) closeBulkDialog(); else setBulkDialogOpen(true); }}>
        <DialogContent className="max-w-lg" data-testid="bulk-loan-dialog">
          <DialogHeader>
            <DialogTitle>Bulk Import Loans</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
              <h4 className="text-sm font-semibold text-blue-900 mb-2">Step 1: Download Template</h4>
              <p className="text-xs text-blue-700 mb-3">Fill entrepreneur name, mobile, loan type, amount, interest rate, tenure, disbursement date, total collected, and status (Active/Closed).</p>
              <p className="text-xs text-amber-700 mb-3 font-medium">Note: Entrepreneurs must be uploaded first before importing loans.</p>
              <Button variant="outline" size="sm" onClick={downloadLoanTemplate} data-testid="download-loan-template">
                <Download className="w-4 h-4 mr-2" /> Download Template (.xlsx)
              </Button>
            </div>
            {isAdmin() && (
              <div>
                <Label className="text-sm font-medium">Step 2: Select Branch *</Label>
                <Select value={bulkBranch} onValueChange={setBulkBranch}>
                  <SelectTrigger className="mt-1" data-testid="bulk-loan-branch-select">
                    <SelectValue placeholder="Select branch" />
                  </SelectTrigger>
                  <SelectContent>
                    {branches.map(b => (
                      <SelectItem key={b.id} value={b.id}>{b.code} - {b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-sm font-medium">{isAdmin() ? 'Step 3' : 'Step 2'}: Upload File *</Label>
              <div className="mt-1 border-2 border-dashed border-slate-300 rounded-lg p-4 text-center hover:border-indigo-400 transition-colors">
                <input type="file" accept=".xlsx,.xls,.csv" onChange={(e) => { setBulkFile(e.target.files[0]); setBulkResult(null); }} className="hidden" id="bulk-loan-input" data-testid="bulk-loan-file-input" />
                <label htmlFor="bulk-loan-input" className="cursor-pointer">
                  <FileUp className="w-8 h-8 mx-auto mb-2 text-slate-400" />
                  {bulkFile ? <p className="text-sm font-medium text-indigo-700">{bulkFile.name}</p> : <p className="text-sm text-slate-500">Click to select Excel (.xlsx) or CSV file</p>}
                </label>
              </div>
            </div>
            <Button onClick={handleBulkLoanUpload} disabled={bulkUploading || !bulkFile || (isAdmin() && !bulkBranch)} className="w-full bg-indigo-700 hover:bg-indigo-800 text-white" data-testid="bulk-loan-submit">
              {bulkUploading ? 'Importing...' : 'Upload & Import Loans'}
            </Button>
            {bulkResult && (
              <div className={`p-4 rounded-lg border ${bulkResult.imported > 0 ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'}`}>
                <h4 className="text-sm font-semibold mb-2 flex items-center gap-2">
                  {bulkResult.imported > 0 ? <><CheckCircle className="w-4 h-4 text-emerald-600" /> Import Complete</> : <><AlertCircle className="w-4 h-4 text-red-600" /> Import Issues</>}
                </h4>
                <div className="grid grid-cols-3 gap-2 text-center mb-2">
                  <div className="bg-white rounded p-2"><p className="text-lg font-bold text-slate-900">{bulkResult.total}</p><p className="text-xs text-slate-500">Total</p></div>
                  <div className="bg-white rounded p-2"><p className="text-lg font-bold text-emerald-700">{bulkResult.imported}</p><p className="text-xs text-emerald-600">Imported</p></div>
                  <div className="bg-white rounded p-2"><p className="text-lg font-bold text-red-600">{bulkResult.skipped}</p><p className="text-xs text-red-500">Skipped</p></div>
                </div>
                {bulkResult.errors?.length > 0 && (
                  <div className="mt-2 max-h-32 overflow-y-auto">
                    {bulkResult.errors.map((err, i) => <p key={`bulk-err-${i}-${(err || '').slice(0, 20)}`} className="text-xs text-red-600 py-0.5">{err}</p>)}
                  </div>
                )}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Loans;
